import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { ExamBoardSpinner } from './ExamBoardSpinner'
import {
  ExamBoards,
  IBookedCourse,
  IBookedCourseRaw,
  ICourseDetails,
  ILesson,
  ISchemaModuleDto,
  ISchemaThemeDto,
  ISchemaTopicDto,
  IUser,
} from '@/types/firebase'
import { CourseOverviewTabKey, IGenericError } from '@/types/shared'
import { notify } from '@/utils/index'
import {
  createBookedCourse,
  updateBookedCourse,
} from '@/firebase/booked-courses'
import {
  CommonBreadCrumbs,
  CommonLoadMoreBtn,
  CommonSkeleton,
  DashboardCardWrapper,
} from '@/components/ui'
import { usePathname, useRouter } from 'next/navigation'
import { useSelector } from 'react-redux'
import selectedCoursesSelector from '@/redux/selectors/selected-courses'
import { ISelectedCoursesState } from '@/types/redux'
import { useDispatch } from 'react-redux'
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import {
  handleCourseDeSelect,
  refetchSelectedCourses,
  setIsLoadingSelectedCourseId,
  setIsLoadingSelectedCourses,
} from '@/redux/slices/selected-courses'
import {
  studentHeaderHeightInPxDesktop,
  studentHeaderHeightInPxMobile,
} from '@/constants/ui'
import { updateUserById } from '@/firebase/users'
import { BaseModal } from '@/components/ui/common/modal'
import { SelectCoursesModalContent } from './select-course-modal'
import {
  firebaseGetLessonsByTopicId,
  getLessonById,
} from '@/firebase/lessons-ts'
import { DocumentSnapshot, Timestamp } from 'firebase/firestore'
import { StudentCourseCard } from './StudentCourseCard'
import {
  extractUncompletedLessonIdsFromTopicByCourseProgress,
  extractUncompletedModulesIdsFromCourseProgressByTheme,
  extractUncompletedTopicsIdsFromModuleByCourseProgress,
} from '@/utils/course-progress'
import { PlusIcon } from '@heroicons/react/24/outline'
import { useSearchParams } from 'next/navigation'

const pageItemsLimit = 4

interface Props {
  student: IUser
}

const StudentCoursesContent: FC<Props> = ({ student }) => {
  const [showSelectCoursesModal, setShowSelectCoursesModal] = useState(false)
  const [isLoadingMoreSelectedCourses, setIsLoadingMoreSelectedCourses] =
    useState(false)

  const coursesListRef = useRef<HTMLUListElement | null>(null)

  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const {
    isLoadingSelectedCourses: isLoading,
    selectedCourses,
    hasMoreSelectedCoursesToDownload,
    lastDocumentSnapshot,
  } = useSelector(selectedCoursesSelector)

  const dispatch = useDispatch()
  const thunkDispatch = dispatch as ThunkDispatch<
    ISelectedCoursesState,
    unknown,
    AnyAction
  >

  const getSelectedCourses = useCallback(
    async (shouldTriggerIsLoading: boolean = true) => {
      if (!student.uid) {
        return
      }

      try {
        shouldTriggerIsLoading && dispatch(setIsLoadingSelectedCourses(true))

        // fetch selected courses and set to redux
        const resultAction = await thunkDispatch(
          refetchSelectedCourses({
            userId: student.uid,
            pageItemsLimit,
          }),
        )

        if (refetchSelectedCourses.rejected.match(resultAction)) {
          throw resultAction.payload
        }
      } catch (error: unknown) {
        console.error('error getSelectedCourses :>> ', {
          message: (error as IGenericError)?.message,
          error,
        })

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        shouldTriggerIsLoading && dispatch(setIsLoadingSelectedCourses(false))
      }
    },
    [dispatch, student.uid, thunkDispatch],
  )

  useEffect(() => {
    getSelectedCourses()
  }, [getSelectedCourses])

  useEffect(() => {
    updateUserById(student.uid, {
      welcomeModal: 0,
    })
    // do not add any dependency, should be triggered once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (searchParams?.get('action') === 'showSelectCoursesModal') {
      if (selectedCourses.length === 0) {
        setShowSelectCoursesModal(true)
      }

      let updatedParams = new URLSearchParams(searchParams)
      updatedParams.delete('action')

      router.replace(pathname + '?' + updatedParams.toString())
    }
  }, [isLoading, pathname, router, searchParams, selectedCourses.length])

  const handleCourseClick = useCallback(
    async (courseDetails: ICourseDetails) => {
      if (!student || !student.uid) {
        return
      }

      try {
        dispatch(setIsLoadingSelectedCourseId(courseDetails.course.id))

        let bookedCourseId: string = courseDetails.bookedCourse.id

        if (!bookedCourseId) {
          const newBookedCourse = await createBookedCourse({
            userId: student.uid,
            course: courseDetails.course,
            partialBookedCourse: {
              examBoard: courseDetails.course.examBoards[0],
            },
          })

          bookedCourseId = newBookedCourse.id
        }

        let path = `/student/${courseDetails.course.id}?bookedId=${bookedCourseId}`

        let targetLessonId: string | undefined =
          courseDetails.bookedCourse.lastVisitedLesson
        let targetThemeId: string | undefined = undefined
        let targetModuleId: string | undefined = undefined
        let targetTopicId: string | undefined = undefined

        if (targetLessonId) {
          const lesson: ILesson | undefined = await getLessonById(
            targetLessonId,
          )

          if (lesson) {
            targetThemeId = lesson.themeId
            targetModuleId = lesson.moduleId
            targetTopicId = lesson.topicId

            if (targetThemeId && targetModuleId && targetTopicId) {
              path += `&tab=${CourseOverviewTabKey.Lesson}&themeId=${targetThemeId}&moduleId=${targetModuleId}&topicId=${targetTopicId}&lessonId=${targetLessonId}`
            }
          }
        }

        router.push(path)
      } catch (error: unknown) {
        console.error('error  :>> ', (error as IGenericError)?.message || error)

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        dispatch(setIsLoadingSelectedCourseId(undefined))
      }
    },
    [dispatch, router, student],
  )

  const handleCourseSelect = useCallback(
    async (
      courseDetails: ICourseDetails,
      examBoard: ExamBoards,
      refetchHandler?: () => Promise<void>,
      startLoadingHandler?: () => void,
      stopLoadingHandler?: () => void,
    ) => {
      if (!student || !student.uid) {
        return
      }

      try {
        startLoadingHandler && startLoadingHandler()

        let bookedCourse: IBookedCourse | undefined = courseDetails.bookedCourse

        const updateObj: Partial<Omit<IBookedCourseRaw, 'userId'>> = {
          isSelected: true,
          examBoard,
          selectionDate: Timestamp.now(),
          lastInteractionDate: Timestamp.now(),
        }

        if (!bookedCourse.id) {
          bookedCourse = await createBookedCourse({
            userId: student.uid,
            course: courseDetails.course,
            partialBookedCourse: updateObj,
          })
        } else {
          await updateBookedCourse(bookedCourse.id, updateObj)

          bookedCourse = { ...bookedCourse, ...updateObj }
        }

        notify('success', `${courseDetails.course.title} course selected!`)

        refetchHandler && (await refetchHandler())
      } catch (error: unknown) {
        console.error(
          'error in handleCourseSelect :>> ',
          (error as IGenericError)?.message || error,
        )

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        stopLoadingHandler && stopLoadingHandler()
      }
    },
    [student],
  )

  const handleLoadMoreSelectedCourses = useCallback(
    async (lastDocumentSnapshot: DocumentSnapshot | null) => {
      if (!student?.uid) {
        return
      }

      try {
        setIsLoadingMoreSelectedCourses(true)

        const resultAction = await thunkDispatch(
          refetchSelectedCourses({
            userId: student.uid,
            lastDocumentSnapshot,
            pageItemsLimit,
          }),
        )

        if (refetchSelectedCourses.rejected.match(resultAction)) {
          throw resultAction.payload
        }
      } catch (error: unknown) {
        console.error(
          'error in handleLoadMoreSelectedCourses :>> ',
          (error as IGenericError)?.message || error,
        )

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        setIsLoadingMoreSelectedCourses(false)
      }
    },
    [student.uid, thunkDispatch],
  )

  const handleThemeClick = useCallback(
    async (targetTheme: ISchemaThemeDto, courseDetails: ICourseDetails) => {
      let targetModuleId: string | undefined = undefined
      let targetTopicId: string | undefined = undefined
      let targetLessonId: string | undefined = undefined

      const { bookedCourse, course } = courseDetails

      if (bookedCourse.courseProgress) {
        const uncompletedModules: ISchemaModuleDto[] =
          extractUncompletedModulesIdsFromCourseProgressByTheme(
            bookedCourse.courseProgress,
            targetTheme.children,
            targetTheme.id,
          )

        for (const uncompletedModule of uncompletedModules) {
          const uncompletedTopics: ISchemaTopicDto[] =
            extractUncompletedTopicsIdsFromModuleByCourseProgress(
              bookedCourse.courseProgress,
              uncompletedModule,
            )

          for (const topic of uncompletedTopics) {
            const lessons: ILesson[] = await firebaseGetLessonsByTopicId(
              course.id,
              topic.id,
            )
            const uncompletedLessons: ILesson[] =
              extractUncompletedLessonIdsFromTopicByCourseProgress(
                bookedCourse.courseProgress,
                lessons,
                topic.id,
              )

            const firstUncompletedLesson = uncompletedLessons[0]

            if (firstUncompletedLesson) {
              targetModuleId = uncompletedModule.id
              targetTopicId = topic.id
              targetLessonId = firstUncompletedLesson.id

              break
            }
          }

          if (targetLessonId) {
            break
          }
        }
      }

      const path = `/student/${course.id}`

      const staticParam = `?bookedId=${bookedCourse.id}`

      let additionalParams = `&tab=${CourseOverviewTabKey.Overview}`

      if (targetModuleId && targetTopicId && targetLessonId) {
        additionalParams = `&tab=${CourseOverviewTabKey.Lesson}&themeId=${targetTheme.id}&moduleId=${targetModuleId}&topicId=${targetTopicId}&lessonId=${targetLessonId}`
      }

      router.push(`${path}${staticParam}${additionalParams}`)
    },
    [router],
  )

  // scroll to bottom on selected courses list length change
  useEffect(() => {
    if (!coursesListRef.current) return

    const isMobile = window.innerWidth <= 768
    const studentHeaderHeight = isMobile
      ? studentHeaderHeightInPxMobile
      : studentHeaderHeightInPxDesktop
    // offset is a bit less then card height (full screen for mobile)
    const offset = window.innerHeight - 2 * studentHeaderHeight

    if (isMobile && selectedCourses.length > pageItemsLimit) {
      // Scroll down by one card height
      window.scrollBy({
        top: offset,
        behavior: 'smooth',
      })
    } else {
      coursesListRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
    }
  }, [selectedCourses.length])

  return (
    <div className="w-full max-w-screen-2xl md:flex">
      <div className="right p-4 lg:py-8 lg:px-4 w-full min-h-[calc(100vh-var(--student-header-height))] flex flex-col gap-4 lg:gap-8">
        {/* Breadcrumbs */}
        <CommonBreadCrumbs
          breadcrumbs={[
            { title: '', href: '/student' },
            { title: 'My Courses' },
          ]}
        />

        {isLoading ? (
          // Skeleton Loader
          <ul className="w-full grid grid-cols-4 lg:grid-cols-8 gap-4">
            {Array.from({ length: 4 }).map((_, i) => {
              return (
                <DashboardCardWrapper
                  key={`course-card-skeleton-${i}`}
                  as="li"
                  className="col-span-4"
                >
                  <CommonSkeleton
                    key={i}
                    additionalBgColorClassName="bg-v8-blue/15"
                    additionalClassName="h-full w-full rounded-[20px]"
                  />
                </DashboardCardWrapper>
              )
            })}
          </ul>
        ) : (
          <>
            {!selectedCourses?.length ? (
              <div className="h-full flex grow justify-center items-center px-4">
                <button
                  type="button"
                  onClick={() => setShowSelectCoursesModal(true)}
                  className="mb-20 w-full max-w-md flex flex-col items-center gap-4 rounded-v8 border border-v8-line bg-white p-8 text-center shadow-v8-card transition hover:-translate-y-0.5 hover:border-v8-blue"
                >
                  <ExamBoardSpinner />
                  <div className="flex flex-col gap-1">
                    <p className="text-xl font-bold text-v8-ink">
                      {'Pick your course'}
                    </p>
                    <p className="text-sm text-v8-mut">
                      {'Video lessons and learning support for your level, subject and exam board, with exam questions and Axi help to accompany.'}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-v8-blue px-5 py-2.5 text-sm font-semibold text-white shadow-v8-btn">
                    <PlusIcon className="w-4 h-4" strokeWidth={2.5} />
                    {'Add a course'}
                  </span>
                </button>
              </div>
            ) : (
              <>
                <ul
                  ref={coursesListRef}
                  className="w-full grid grid-cols-4 lg:grid-cols-8 gap-4"
                >
                  {selectedCourses.map((courseDetails) => {
                    let examBoard: ExamBoards =
                      courseDetails.course.examBoards[0]

                    const studentsExamBoard: ExamBoards | undefined =
                      student.examBoard

                    const bookedCourseExamBoard: ExamBoards | undefined =
                      courseDetails.bookedCourse.examBoard

                    if (
                      studentsExamBoard &&
                      courseDetails.course.examBoards.includes(
                        studentsExamBoard,
                      )
                    ) {
                      examBoard = studentsExamBoard
                    }

                    if (bookedCourseExamBoard) {
                      examBoard = bookedCourseExamBoard
                    }

                    return (
                      <StudentCourseCard
                        key={courseDetails.course.id}
                        courseDetails={courseDetails}
                        isLoadingSelectedCourse={false}
                        handleCourseClick={() =>
                          handleCourseClick(courseDetails)
                        }
                        examBoard={examBoard}
                        handleListSlideThemeClick={(theme) =>
                          handleThemeClick(theme, courseDetails)
                        }
                      />
                    )
                  })}
                </ul>

                {hasMoreSelectedCoursesToDownload && (
                  <div
                    id={'load-more'}
                    className="w-full flex items-center justify-center"
                  >
                    <CommonLoadMoreBtn
                      onClick={async () =>
                        await handleLoadMoreSelectedCourses(
                          lastDocumentSnapshot,
                        )
                      }
                      disabled={isLoadingMoreSelectedCourses}
                      className="w-auto min-w-[130px] h-8 px-4 py-1 flex items-center justify-center gap-2 text-sm text-center leading-4 text-white font-semibold border border-transparent rounded-md bg-v8-blue disabled:opacity-70 disabled:cursor-not-allowed disabled:pointer-events-none hover:bg-white hover:border-v8-blue hover:text-v8-blue transition ease-in-out duration-500"
                      isLoading={isLoadingMoreSelectedCourses}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Select Courses Modal */}
        <BaseModal
          id="no-courses-select-course-modal"
          key="no-courses-select-course-modal"
          isOpen={showSelectCoursesModal}
          onClose={() => setShowSelectCoursesModal(false)}
          content={
            <SelectCoursesModalContent
              student={student}
              handleCourseSelect={handleCourseSelect}
              handleCourseDeSelect={handleCourseDeSelect}
              handleModalClose={() => setShowSelectCoursesModal(false)}
            />
          }
          bodyBasicClassName="relative overflow-hidden transition-all transform bg-v8-paper rounded-v8 border border-v8-line shadow-v8-card p-6"
          bodyAdditionalClassName={`h-[calc(100vh-80px)] !px-4`}
          customZIndexClass="z-[655]"
        />
      </div>
    </div>
  )
}

export default StudentCoursesContent
